
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import BaseFilterable from '@/components/reports-v3/components/filterables/BaseFilterable.vue';
import {
	PermissionsGroup,
} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import moment from 'moment';
import { addComma } from '@/util/number';
import DataInfo from '@/components/reports-v3/components/elements/DataInfo.vue';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		BaseFilterable,
		DataInfo,
	},
})
export default class ProjectGeneralTable extends FilterWidget {
	@Prop({ default: () => '' }) public readonly selectedProjectCode!: string;

	public get permissionIds(): PermissionsGroup[] {
		return ['transactions'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public projectPeriod: number = 0;
	public pastDays: number = 0;
	public projectValue: number = 0;
	public formattedProjectValue: string = '';
	public budgetCost: string = '';
	public projectStatus: string = '';

	public ytdCost = 0;
	public ytdCollection = 0;
	public ytdBilling = 0;
	public currentMonthCost = 0;
	public currentMonthBilling = 0;
	public currentMonthCollection = 0;
	public totalCollection = 0;
	public totalCost = 0;
	public totalBilling = 0;
	public formattedTotalBilling = '';

	@Watch('projectInfo', { immediate: true })
	public onProjectDetailsChange(newVal) {
		this.$emit('returnProjectDetails', newVal);
	}

	public formatNumber(value: number) {
		return this.currencySymbol + ' ' + addComma(value, this.numDecimal, true);
	}

	public get projectInfo() {
		return {
			projectPeriod: this.projectPeriod,
			pastDays: this.pastDays,
			projectStatus: this.projectStatus,
			formattedProjectValue: this.formattedProjectValue,
			budgetCost: this.budgetCost,
		};
	}

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get numDecimal() {
		return gdbx.numDecimal;
	}

	public get dateFormatted() {
		return [];
	}

	public get expensiveHook() {
		const { selectedProjectCode, selectedAsOfDate } = this;
		return JSON.stringify([selectedProjectCode, selectedAsOfDate]);
	}

	public async expensiveCalc() {
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const projTable = getTableName('globalProjects');
		const cfTable = getTableName('projectCashFlow');
		const projectCode = escapeStr(this.selectedProjectCode);

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(
			['globalProjects', 'projectCashFlow'],
		);
		this.checkInterrupted(jobId);

		this.generateLoadingText(0.3);

		// Parallel: project details + cash flow aggregation
		const ytdYear = moment(this.selectedAsOfDate).year();
		const currentMonth =
			moment(this.selectedAsOfDate).startOf('month').month() + 1;

		const costExpr = `(TRY_CAST("STOCKISSUE" AS DOUBLE)`
			+ ` + TRY_CAST("STOCKRETURN" AS DOUBLE)`
			+ ` + TRY_CAST("OTHERSTOCK" AS DOUBLE)`
			+ ` + TRY_CAST("EXPENSES" AS DOUBLE)`
			+ ` + TRY_CAST("SUBCON" AS DOUBLE)`
			+ ` + TRY_CAST("LABOUR" AS DOUBLE))`;
		const billingExpr = `(TRY_CAST("CUSTOMERINVOICE" AS DOUBLE)`
			+ ` + TRY_CAST("CUSTOMERDEBIT" AS DOUBLE)`
			+ ` + TRY_CAST("CUSTOMERCREDIT" AS DOUBLE))`;
		const collectionExpr = `(TRY_CAST("CUSTOMERPAYMENT" AS DOUBLE)`
			+ ` + TRY_CAST("CUSTOMERCONTRA" AS DOUBLE)`
			+ ` + TRY_CAST("CUSTOMERREFUND" AS DOUBLE))`;

		const [projectResult, cfResult] = await Promise.all([
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					TRY_CAST("PROJECTVALUE" AS DOUBLE) as projectValue,
					TRY_CAST("PROJECTCOST" AS DOUBLE) as projectCost
				FROM ${projTable}
				WHERE "CODE" = '${projectCode}'
			`),
			queryHead.rawQuery(`
				SELECT
					SUM(CASE WHEN yr = ${ytdYear} AND mo <= ${currentMonth}
						THEN cost ELSE 0 END) as ytdCost,
					SUM(CASE WHEN yr = ${ytdYear} AND mo <= ${currentMonth}
						THEN billing ELSE 0 END) as ytdBilling,
					SUM(CASE WHEN yr = ${ytdYear} AND mo <= ${currentMonth}
						THEN collection ELSE 0 END) as ytdCollection,
					SUM(CASE WHEN yr = ${ytdYear} AND mo = ${currentMonth}
						THEN cost ELSE 0 END) as cmCost,
					SUM(CASE WHEN yr = ${ytdYear} AND mo = ${currentMonth}
						THEN billing ELSE 0 END) as cmBilling,
					SUM(CASE WHEN yr = ${ytdYear} AND mo = ${currentMonth}
						THEN collection ELSE 0 END) as cmCollection,
					SUM(CASE WHEN yr < ${ytdYear}
						OR (yr = ${ytdYear} AND mo <= ${currentMonth})
						THEN cost ELSE 0 END) as totalCost,
					SUM(CASE WHEN yr < ${ytdYear}
						OR (yr = ${ytdYear} AND mo <= ${currentMonth})
						THEN billing ELSE 0 END) as totalBilling,
					SUM(CASE WHEN yr < ${ytdYear}
						OR (yr = ${ytdYear} AND mo <= ${currentMonth})
						THEN collection ELSE 0 END) as totalCollection
				FROM (
					SELECT TRY_CAST("YEAR" AS INT) as yr,
						TRY_CAST("MONTH" AS INT) as mo,
						${costExpr} as cost,
						${billingExpr} as billing,
						${collectionExpr} as collection
					FROM ${cfTable}
					WHERE "PROJECT" = '${projectCode}'
				) sub
			`),
		]);
		this.checkInterrupted(jobId);

		// Project details
		this.projectPeriod = 0;
		this.pastDays = 0;
		this.formattedProjectValue = '';
		this.budgetCost = '';
		this.projectStatus = '';

		if (projectResult.length > 0) {
			const p = projectResult[0];
			this.projectStatus = '';
			this.projectValue = Number(p.projectValue) || 0;
			this.formattedProjectValue =
				this.currencySymbol + ' '
				+ addComma(this.projectValue, this.numDecimal, true);
			this.budgetCost =
				this.currencySymbol + ' '
				+ addComma(Number(p.projectCost) || 0, this.numDecimal, true);
		}

		// Cash flow aggregation
		const cf = cfResult[0] || {};
		this.ytdCost = Number(cf.ytdCost) || 0;
		this.ytdBilling = Number(cf.ytdBilling) || 0;
		this.ytdCollection = Number(cf.ytdCollection) || 0;
		this.currentMonthCost = Number(cf.cmCost) || 0;
		this.currentMonthBilling = Number(cf.cmBilling) || 0;
		this.currentMonthCollection = Number(cf.cmCollection) || 0;
		this.totalCost = Number(cf.totalCost) || 0;
		this.totalBilling = Number(cf.totalBilling) || 0;
		this.totalCollection = Number(cf.totalCollection) || 0;
		this.formattedTotalBilling =
			this.currencySymbol + ' '
			+ addComma(this.totalBilling, this.numDecimal, true);

		this.generateLoadingText(1);
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
